/**
 * Módulo Comunidades.
 * @module Comunidades
 * @see Comunidades
 */

import { get } from "firebase/database";
import { Semaforito } from "app/components/Semaforito/Semaforito";
import { PDFDocument, rgb } from "pdf-lib";
import { CurrencyFormatter } from "app/components/helpers/Views";
import { initializeApp, deleteApp } from "firebase/app";
import { getDatabase, ref as firebaseRef } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";

export const insertarEnAdminCommunities = async (
  idcomunidad,
  despachoActual,
  firebaseConfigAdmin,
  firebase
) => {
  const secundariaApp = initializeApp(firebaseConfigAdmin, "secundaria");
  const database = getDatabase(secundariaApp);
  try {
    const functions = getFunctions(firebase.app, process.env.REACT_APP_FIREBASE_ZONE);
    var addCommunityToAdmin = httpsCallable(functions, "addCommunityToAdmin");

    const res = await addCommunityToAdmin({
      community: idcomunidad,
      desk: despachoActual,
    });

    return res;
  } catch (error) {
    console.log("error al crear la comunidad en admin");
    return false;
  } finally {
    await deleteApp(secundariaApp);
  }
};

/**
 * Obtiene el nombre de una comunidad específica desde Firebase.
 *
 * Esta función realiza una consulta a Firebase en la ruta `Comunidad/{idComunidad}/Nombre`
 * para recuperar el nombre almacenado de una comunidad concreta.
 *
 * @async
 * @function getNombreComunidad
 * @param {Object} firebase - Instancia del módulo de Firebase configurado en el proyecto.
 * @param {string} idComunidad - Identificador único de la comunidad cuyo nombre se desea obtener.
 * @returns {Promise<string|null>} El nombre de la comunidad si existe, o `null` si no se encuentra.
 *
 * @example
 * const nombre = await getNombreComunidad(firebase, 'comu123');
 * console.log(nombre); // "Residencial Las Palmeras"
 */

export const getNombreComunidad = async (firebase, idComunidad) => {
  const snapshot = await get(firebase.getReferencia(`Comunidad/${idComunidad}/Nombre`, null));
  const nombre = snapshot.val();
  return nombre;
};
/**
 * Descarga un contrato en formato PDF con los datos proporcionados.
 *
 * Esta función carga un archivo PDF, rellena los campos del formulario con los datos del contrato,
 * y luego permite descargar el archivo PDF modificado.
 *
 * @async
 * @function descargarPDFContrato
 * @param {Object} contrato - Objeto que contiene los datos del contrato.
 * @param {string} contrato.plan - Nombre del plan contratado.
 * @param {number} contrato.precioSelected - Precio seleccionado para el contrato.
 * @param {string} contrato.formapago - Forma de pago seleccionada para el contrato.
 * @param {number} contrato.vecinos - Número de vecinos asociados al contrato.
 * @param {string} nombreFichero - Nombre del archivo que se descargará con la extensión `.pdf`.
 *
 * @returns {Promise<void>} No retorna valor, solo dispara el proceso de descarga del archivo PDF.
 *
 * @example
 * const contrato = {
 *   plan: 'Basico',
 *   precioSelected: 100,
 *   formapago: 'mensual',
 *   vecinos: 20
 * };
 * descargarPDFContrato(contrato, 'Contrato_Las_Palmeras');
 */
export const descargarPDFContrato = async (contrato, nombreFichero) => {
  console.log("Descargando el contrato con los datos:", contrato);

  // 1. Cargar el archivo PDF como array buffer
  const url = "/assets/files/contratocomu.pdf"; // Ruta al PDF
  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());

  // 2. Cargar el PDF con pdf-lib
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  // Obtener el formulario
  const form = pdfDoc.getForm();

  // Obtener todos los campos de formulario
  const fields = form.getFields();
  fields.forEach((field) => {
    console.log("Campo:", field.getName());
  });

  // Extraer los valores del contrato con valores por defecto
  const {
    cuentaBancaria = "",
    fechaInicio = "",
    formapago = "",
    iban = "",
    plan = "",
    precioSelected = "",
    vecinos = ""
  } = contrato || {};

  // Parsear fecha si existe
  let dia = "", mes = "", anio = "";
  if (fechaInicio) {
    const partesFecha = fechaInicio.split(",")[0].split("/");
    if (partesFecha.length === 3) {
      dia = partesFecha[0];
      mes = partesFecha[1];
      anio = partesFecha[2];
    }
  }

  // Función auxiliar para establecer el valor de un campo de texto de forma segura
  const setTextFieldSafely = (fieldName, value) => {
    try {
      const field = form.getTextField(fieldName);
      if (field && value !== undefined && value !== null && value !== "") {
        field.setText(value.toString());
        console.log(`Campo ${fieldName} establecido con valor: ${value}`);
      }
    } catch (error) {
      console.log(`Campo ${fieldName} no encontrado en el PDF o error al establecerlo:`, error.message);
    }
  };

  // Llenar los campos del PDF con los datos disponibles
  try {
    // Campo de precio
    if (precioSelected) {
      setTextFieldSafely("Precio", CurrencyFormatter(precioSelected.toString()));
    }

    // Campo de plan
    if (plan) {
      setTextFieldSafely("plan", plan.charAt(0).toUpperCase() + plan.slice(1));
    }

    // Campo de forma de pago
    if (formapago !== "") {
      const formaPagoTexto = formapago === "0" ? "Transferencia" : "Domiciliación";
      setTextFieldSafely("formapago", formaPagoTexto);
    }

    // Campo de IBAN
    if (iban) {
      setTextFieldSafely("IBAN", iban);
    }

    // Campo de cuenta bancaria
    if (cuentaBancaria) {
      setTextFieldSafely("CuentaBancaria", cuentaBancaria);
    }

    // Campo de vecinos
    if (vecinos !== "") {
      setTextFieldSafely("vecinos", vecinos.toString());
    }

    // Campos de fecha
    if (dia) {
      setTextFieldSafely("Dia", dia);
    }

    if (mes) {
      setTextFieldSafely("Mes", mes);
    }

    if (anio) {
      setTextFieldSafely("Anio", anio);
    }

    // Campos adicionales que pueden existir en el PDF pero no en el contrato actual
    // Se dejan vacíos o con valores por defecto
    const camposAdicionales = [
      "CalleComu",
      "NombreAdmin",
      "Email",
      "DNIadmin",
      "Telefono",
      "NombreComunidad",
      "Ciudad",
      "CodigoPostal",
      "Provincia"
    ];

    camposAdicionales.forEach(campo => {
      setTextFieldSafely(campo, "");
    });

  } catch (error) {
    console.error("Error general al llenar campos del PDF:", error);
  }

  form.flatten();
  const pages = pdfDoc.getPages();
  const firstPage = pages[3];

  // 4. Guardar el PDF modificado
  const pdfBytes = await pdfDoc.save();

  // Descargar el PDF
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  if(nombreFichero == undefined && fechaInicio) {
    // Extraer solo la fecha sin la hora
    const fechaFormateada = fechaInicio.split(",")[0];
    nombreFichero = "contato-" + fechaFormateada;
  }
  link.download = nombreFichero + ".pdf";
  link.click();
};
/**
 * Obtiene los datos completos de una comunidad desde Firebase a partir de su ID.
 *
 * @async
 * @function getComunidad
 * @param {object} firebase - Objeto de Firebase que proporciona métodos para acceder a la base de datos.
 * @param {string} idComunidad - Identificador único de la comunidad que se desea obtener.
 * @returns {Promise<Object|null>} Retorna un objeto con los datos de la comunidad o `null` si no existe.
 *
 * @example
 * const comunidad = await getComunidad(firebase, "comu123");
 * console.log(comunidad.Nombre); // "Residencial Sol"
 */
export const getComunidad = async (firebase, idComunidad) => {
  const snapshot = await get(firebase.getReferencia(`Comunidad/${idComunidad}`, null));
  const comunidad = snapshot.val();
  return comunidad;
};
/**
 * Obtiene todas las comunidades no borradas desde Firebase.
 *
 * @async
 * @function getTodasComunidades
 * @param {object} firebase - Objeto que contiene los métodos para acceder a la base de datos de Firebase.
 * @returns {Promise<Array<Object>>} Retorna un array de objetos de comunidades, cada uno con su `idInterno` y número de `Vecinos` calculado.
 *
 * @example
 * const comunidades = await getTodasComunidades(firebase);
 * console.log(comunidades.length); // Número de comunidades activas
 */
export const getTodasComunidades = async (firebase) => {
  const comunidades = [];
  const snapshot = await get(firebase.getReferencia("Comunidad", null));
  snapshot.forEach((childSnapshot) => {
    let cm = childSnapshot.val();
    cm.idInterno = childSnapshot.key;
    if (!cm.Borrado) {
      let numViviendas = 0;
      if (cm.Instancias) {
        numViviendas = cm.Instancias.filter((instancia) => instancia.Usuario != "").length;
      }
      cm.Vecinos = numViviendas;

      comunidades.push(cm);
    }
  });
  return comunidades;
};
/**
 * Obtiene un listado de todas las comunidades desde Firebase, devolviendo un objeto
 * donde cada clave es el ID de la comunidad y el valor es su nombre.
 *
 * @async
 * @function getComunidades
 * @param {object} firebase - Objeto que contiene los métodos para interactuar con Firebase.
 * @returns {Promise<Object>} Retorna un objeto donde las claves son los IDs de las comunidades
 * y los valores son los nombres de cada comunidad.
 *
 * @example
 * const comunidades = await getComunidades(firebase);
 * console.log(comunidades["1234"]); // "Nombre de la comunidad"
 */
export const getComunidades = async (firebase) => {
  const comunidades = [];
  const snapshot = await get(firebase.getReferencia("Comunidad", null));
  snapshot.forEach((childSnapshot) => {
    comunidades[childSnapshot.key] = childSnapshot.val().Nombre;
  });
  return comunidades;
};
/**
 * Determina el color del semáforo y el texto explicativo para una comunidad en función de su estado.
 *
 * La lógica del semáforo se basa en:
 * - Verde: Comunidad activa con el IBAN correcto.
 * - Amarillo: Comunidad activa con IBAN diferente o comunidad inactiva.
 * - Rojo: Comunidad bloqueada.
 *
 * Los resultados se reflejan visualmente mediante el componente `Semaforito`.
 *
 * @function getSemaforoComunidad
 * @param {Object} rowData - Objeto que contiene la información de la comunidad (alta, bloqueo, IBAN, contrato).
 * @param {Object} diccionario - Diccionario de textos traducidos para mostrar el texto explicativo correcto.
 * @param {boolean} rowData.Alta - Indica si la comunidad está activa.
 * @param {boolean} rowData.bloqueada - Indica si la comunidad está bloqueada.
 * @param {string} rowData.IBAN - IBAN actual de la comunidad.
 * @param {string} rowData.ibanDespacho - IBAN asociado al despacho.
 * @param {Object} rowData.ContratoActivo - Contrato activo que contiene la cuenta bancaria.
 * @param {string} rowData.ContratoActivo.cuentaBancaria - IBAN del contrato activo.
 * @returns {JSX.Element} Componente `<Semaforito />` con el color y texto explicativo definidos.
 *
 * @example
 * const semaforo = getSemaforoComunidad(comunidad, diccionario);
 */
export const getSemaforoComunidad = (rowData, diccionario) => {
  console.log("getSemaforoComunidad", rowData);
  const camposComprobarImportantes = ["Poliza", "Seguro"];
  const camposAcomprobar = [
    "Nombre",
    "Cif",
    "Direccion",
    "Poblacion",
    "Provincia",
    "Cp",
    "Vecinos",
  ];
  let colorSeleccionado = "green";
  let ArrayFallos = [];
  camposAcomprobar.forEach((campo) => {
    if (!rowData[campo] || rowData[campo] === "") {
      colorSeleccionado = "yellow";
      ArrayFallos.push(diccionario.comunidadFalta + campo);
    }
  });

  let textoExplicativo = diccionario.comunidadActiva;
  let ibanContrato = rowData?.ContratoActivo?.cuentaBancaria || "";

  if (
    rowData.Alta === true &&
    ibanContrato != rowData.IBAN &&
    ibanContrato != rowData.ibanDespacho
  ) {
    colorSeleccionado = "yellow";
    ArrayFallos.push(diccionario.comunidadIbanDiferente);
    textoExplicativo = diccionario.comunidadIbanDiferente;
  }

  if (rowData.Alta === false) {
    colorSeleccionado = "yellow";
    ArrayFallos.push(diccionario.comunidadInactiva);
    textoExplicativo = diccionario.comunidadInactiva;
  }
  if (rowData.bloqueada === true) {
    colorSeleccionado = "red";
    ArrayFallos.push(diccionario.comunidadBloqueada);
    textoExplicativo = diccionario.comunidadBloqueada;
  }
  camposComprobarImportantes.forEach((campo) => {
    if (!rowData[campo] || rowData[campo] === "") {
      colorSeleccionado = "red";
      ArrayFallos.push(
        diccionario.comunidadFaltaImportante + campo + diccionario.comunidadFaltaImportante2
      );
    }
  });

  rowData.colorSeleccionado = colorSeleccionado;
  // rowData.textoExplicativo = textoExplicativo;
  textoExplicativo = ArrayFallos.length > 0 ? ArrayFallos.join(" || ") : textoExplicativo;
  rowData.textoExplicativo = textoExplicativo;
  return (
    <Semaforito
      rowdata={rowData}
      colorSeleccionado={colorSeleccionado}
      textoExplicativo={textoExplicativo}
    />
  );
};
